import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material'
import { useStyles } from './MeteringDimensions.styles'
import clsx from 'clsx'
import {
  CheckBoxFieldv2,
  InputStartAdornment,
  TextInputV4,
} from '@labrav/react-components'
import { cloneDeep, get, isEmpty } from 'lodash'
import { isNonEmptyNumber } from '@labrav/common'
import {
  AdditionalMeter,
  AzureOfferPlan,
  IncludedQuantity,
} from '../../../../../modules/privateOffer/reducer'
import { AzurePricingType } from '../../../../../modules/privateOffer/reducer'
import { getDiscountedPriceValue } from '../../../../../../common/utils/getDiscountedPriceValue'
import { FormikProps, useFormikContext } from 'formik'
import { numberFormat } from '../../../../../../common/utils/numberFormat'

export interface MeteringDimensionsProps {
  plan: AzureOfferPlan
  index: number
}

export const MeteringDimensions = ({
  plan,
  index,
}: MeteringDimensionsProps) => {
  const { classes } = useStyles()

  const { values, setFieldValue, errors } = useFormikContext() as FormikProps<
    Record<string, unknown>
  >

  const azurePricingType = values?.azurePricingType
  const isDiscountPricing =
    azurePricingType === AzurePricingType.LABRA_PERCENTAGE

  const planPrices = plan?.pricing?.recurrentPrice?.prices || []

  const shouldReadOnlyValue = isDiscountPricing

  const dynamicColumnForMetering = planPrices.map(
    planPrice =>
      `Base quantity (${planPrice.billingTerm.value} ${planPrice.billingTerm.type})`
  )

  const meteringList = { ...plan.pricing.customMeters?.meters }

  const meteringColumn = [
    'Name',
    'Unit',
    `${isDiscountPricing ? 'Discounted price' : 'Price'} per unit`,
    ...dynamicColumnForMetering,
  ]

  const updatePlan = (meteringKey: string, updatedMeter: AdditionalMeter) => {
    if (plan?.pricing?.customMeters) {
      const customMetersData = {
        ...plan?.pricing?.customMeters,
        meters: {
          ...plan?.pricing?.customMeters.meters,
          [meteringKey]: updatedMeter,
        },
      }
      const updateplan = {
        ...plan,
        pricing: {
          ...plan.pricing,
          customMeters: {
            ...customMetersData,
          },
        },
      }
      const planList = cloneDeep(values.plans) as AzureOfferPlan[]
      planList[index] = {
        ...updateplan,
      }
      setFieldValue('plans', planList)
    }
  }

  const tableDataCellClass = clsx(classes.rowText, classes.tableCell)

  return (
    <div>
      <div className={classes.headingWrapper}>
        <div className={classes.plansHeading}>Metering dimensions</div>
      </div>

      <div>
        <TableContainer>
          <Table aria-label="simple table">
            <TableHead className={classes.tableHader}>
              <TableRow>
                {meteringColumn.map((col, index) => (
                  <TableCell
                    key={`metering-col-${index}-${col}`}
                    className={clsx(
                      classes.headerTitle,
                      classes.tableCell,
                      index > 1 ? classes.textRight : ''
                    )}
                    data-testid={'metering-column'}
                  >
                    {col}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {meteringList &&
                Object.keys(meteringList).map(meteringKey => {
                  const meter = { ...meteringList[meteringKey] }
                  const meterErrorObject = get(
                    errors?.plans,
                    `[${index}].pricing.customMeters.meters.[${meteringKey}]`,
                    {}
                  )

                  const updateMeter =
                    (
                      key: 'pricePerPaymentInUsd' | 'quantity' | 'isInfinite',
                      meterQuantityIndex?: number
                    ) =>
                    (
                      event: React.ChangeEvent<HTMLInputElement>,
                      checked?: boolean
                    ) => {
                      const updatedIncludedQuantities = [
                        ...meter.includedQuantities,
                      ]
                      if (meterQuantityIndex !== undefined) {
                        updatedIncludedQuantities[meterQuantityIndex] = {
                          ...updatedIncludedQuantities[meterQuantityIndex],
                          [key]:
                            key === 'isInfinite'
                              ? checked
                              : parseInt(event.target.value),
                          ...(key === 'isInfinite' && checked
                            ? { quantity: undefined }
                            : {}),
                        }
                      }
                      const updatedMeter = {
                        ...meter,
                        ...(key === 'pricePerPaymentInUsd'
                          ? {
                              pricePerPaymentInUsd:
                                event.target.value !== ''
                                  ? Number(event.target.value)
                                  : event.target.value,
                            }
                          : {}),
                        includedQuantities: [...updatedIncludedQuantities],
                      }
                      updatePlan(meteringKey, updatedMeter as AdditionalMeter)
                    }
                  return (
                    <TableRow className={classes.row} key={meteringKey}>
                      <TableCell className={tableDataCellClass} width={'150px'}>
                        <div>{meter.meteringDimensionName}</div>
                      </TableCell>
                      <TableCell className={tableDataCellClass} width={'120px'}>
                        <div>{meter.unitOfMeasure}</div>
                      </TableCell>
                      <TableCell
                        className={clsx(tableDataCellClass, classes.textRight)}
                        width={'220px'}
                      >
                        {isDiscountPricing ? (
                          <DisplayValueData
                            isPrice={true}
                            id="pricePerPaymentInUsd"
                            value={meter.pricePerPaymentInUsd}
                          />
                        ) : (
                          <TextInputV4
                            type="number"
                            title=""
                            placeHolder=""
                            isReadOnly={
                              !!meter.includedQuantities?.[0]?.isInfinite
                            }
                            errorText={
                              get(
                                meterErrorObject,
                                'pricePerPaymentInUsd',
                                ''
                              ) as string
                            }
                            isError={
                              !isEmpty(
                                get(
                                  meterErrorObject,
                                  'pricePerPaymentInUsd',
                                  ''
                                ) as string
                              )
                            }
                            startAdornment={<InputStartAdornment text="$" />}
                            alignRight
                            isRequired={true}
                            value={
                              isNonEmptyNumber(meter.pricePerPaymentInUsd)
                                ? meter.pricePerPaymentInUsd
                                : ''
                            }
                            updatedValue={
                              isNonEmptyNumber(meter.pricePerPaymentInUsd)
                                ? meter.pricePerPaymentInUsd
                                : ''
                            }
                            onChange={updateMeter('pricePerPaymentInUsd')}
                            keyName={`plans.${index}.pricing.customMeters.meters.${meteringKey}.pricePerPaymentInUsd`}
                            variant="regularV2"
                            keyToPrevent={['+', '-', 'e']}
                            showSpinner={false}
                          />
                        )}
                      </TableCell>
                      {meter.includedQuantities.map(
                        (
                          qunatityItem: IncludedQuantity,
                          quantityIndex: number
                        ) => {
                          const errQunatity = get(
                            meterErrorObject,
                            `includedQuantities.[${quantityIndex}].quantity`,
                            ''
                          )

                          return (
                            <TableCell
                              className={clsx(
                                tableDataCellClass,
                                classes.textRight
                              )}
                              width={'220px'}
                              key={`quantity-${qunatityItem?.billingTerm?.type}-${qunatityItem?.billingTerm?.value ?? quantityIndex}`}
                            >
                              {!shouldReadOnlyValue ? (
                                <div className={classes.tableInput}>
                                  <TextInputV4
                                    type="number"
                                    title={''}
                                    placeHolder={''}
                                    alignRight
                                    errorText={(errQunatity || '') as string}
                                    isError={
                                      !qunatityItem.isInfinite &&
                                      !isEmpty(errQunatity || '')
                                    }
                                    isReadOnly={qunatityItem.isInfinite}
                                    isRequired={true}
                                    value={
                                      isNonEmptyNumber(qunatityItem.quantity)
                                        ? qunatityItem.quantity
                                        : ''
                                    }
                                    updatedValue={
                                      isNonEmptyNumber(qunatityItem.quantity)
                                        ? qunatityItem.quantity
                                        : ''
                                    }
                                    onChange={updateMeter(
                                      'quantity',
                                      quantityIndex
                                    )}
                                    keyName={`plans.${index}.pricing.customMeters.meters.${meteringKey}.includedQuantities.${quantityIndex}.quantity`}
                                    minNumber={0}
                                    variant="regularV2"
                                    showSpinner={false}
                                  />
                                  <div className={classes.checkbox}>
                                    <CheckBoxFieldv2
                                      keyName="isInfinite"
                                      value={qunatityItem.isInfinite || false}
                                      color="default"
                                      checked={Boolean(
                                        qunatityItem.isInfinite || false
                                      )}
                                      title={
                                        <span className={classes.checkboxTitle}>
                                          Unlimited
                                        </span>
                                      }
                                      name="isInfinite"
                                      id="isInfinite"
                                      onChange={updateMeter(
                                        'isInfinite',
                                        quantityIndex
                                      )}
                                      inputProps={
                                        {
                                          'data-testid': 'checkbox-item',
                                        } as React.InputHTMLAttributes<HTMLInputElement>
                                      }
                                    />
                                  </div>
                                </div>
                              ) : (
                                <DisplayValueData
                                  id="baseQuantity"
                                  value={qunatityItem.quantity}
                                  isUnlimited={qunatityItem?.isInfinite}
                                />
                              )}
                            </TableCell>
                          )
                        }
                      )}
                    </TableRow>
                  )
                })}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    </div>
  )
}

export const DisplayValueData = ({
  value,
  id,
  isPrice,
  isUnlimited,
  precision = 3,
}: {
  value?: number
  id: string
  isPrice?: boolean
  isUnlimited?: boolean
  precision?: number
}) => {
  const { classes } = useStyles()
  return (
    <p className={classes.description} data-testid={`display-${id}`}>
      {isUnlimited ? (
        'Unlimited'
      ) : (
        <>
          {isPrice ? <span className={classes.currency}>$</span> : ''}
          {isNonEmptyNumber(value)
            ? isPrice
              ? numberFormat(value!, precision)
              : value
            : '-'}
        </>
      )}
    </p>
  )
}
